import React, { useEffect, useMemo, useState } from 'react';
import { useDocumentBreadcrumb } from '@/contexts/PageBreadcrumbContext';
import { useQuery } from '@tanstack/react-query';
import api from '@/services/api.service';
import { Link, useNavigate, useLocation, useParams } from 'react-router-dom';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
} from 'antd';
import { ArrowLeftOutlined, MailOutlined, PhoneOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { useUsers } from '@/hooks/useUsers';
import { salesOwnerUserListFilters } from '@/constants/sales-owner-role-codes';
import { useLead, useCreateLead, useUpdateLead } from '@/hooks/useLeads';
import leadService, {
  LEAD_CLOSED_STATUSES,
  LEAD_STATUS_REASONS,
  type LeadDuplicateMatches,
  type LeadInput,
} from '@/services/lead.service';
import CrmRecordPicker, { type CrmRecordLink } from '@/components/crm/activities/CrmRecordPicker';
import DocumentList from '@/components/documents/DocumentList';
import DocumentUpload from '@/components/documents/DocumentUpload';
import { listAssignableSalesTeams } from '@/services/salesTeam.service';
import { useAuthStore } from '@/store/authStore';

const { Title, Text } = Typography;
const { TextArea } = Input;

const LEAD_STATUSES = ['New', 'Contacted', 'Qualified', 'Unqualified', 'Lost'] as const;
const PRIORITIES = ['Low', 'Medium', 'High', 'Critical'] as const;

function useDebouncedValue<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  const key = JSON.stringify(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, ms]);
  return v;
}

const DuplicateList: React.FC<{ matches: LeadDuplicateMatches }> = ({ matches }) => (
  <Space direction="vertical" size={2}>
    {matches.leads.map((l) => (
      <Text key={l.id}>
        Lead <Link to={`/growth/leads/${l.id}`} target="_blank">{l.lead_number}</Link> — {l.customer_name}
        {l.owner_name ? ` (${l.owner_name})` : ''} <Tag>{l.lead_status}</Tag>
        <Text type="secondary">matches {l.matched_on.join(', ')}</Text>
      </Text>
    ))}
    {matches.customers.map((c) => (
      <Text key={c.id}>
        Customer <Link to={`/growth/customers/${c.id}/360`} target="_blank">{c.customer_code || c.customer_name}</Link> —{' '}
        {c.customer_name} <Text type="secondary">matches {c.matched_on.join(', ')}</Text>
      </Text>
    ))}
  </Space>
);

const LeadFormPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: editId } = useParams<{ id: string }>();
  const isCreate = location.pathname.endsWith('/new');
  const entityId = isCreate ? undefined : editId;

  const [form] = Form.useForm();
  const currentUserId = useAuthStore((s) => s.user?.id);
  const { data: revenueServiceTypes = [] } = useQuery({
    queryKey: ['sales-revenue-service-types', 'active'],
    queryFn: async () => {
      const res = await api.get('/sales/revenue-service-types?only_active=true');
      const body = (res as any)?.data ?? res;
      const list = Array.isArray(body) ? body : Array.isArray(body?.data) ? body.data : [];
      return list as { id: string; service_code: string; service_name: string }[];
    },
  });
  const rstOptions = useMemo(
    () => revenueServiceTypes.map((r) => ({ value: r.id, label: `${r.service_code} — ${r.service_name}` })),
    [revenueServiceTypes]
  );
  const { data: salesTeams = [] } = useQuery({
    queryKey: ['sales-teams-assignable'],
    queryFn: listAssignableSalesTeams,
  });
  const teamOptions = useMemo(
    () => salesTeams.map((t) => ({ value: t.id, label: t.team_name })),
    [salesTeams]
  );
  const { data: lead, isLoading: loadingLead } = useLead(entityId);
  const { data: users = [] } = useUsers(
    salesOwnerUserListFilters(isCreate ? currentUserId : lead?.assigned_to || currentUserId)
  );
  const createLead = useCreateLead();
  const updateLead = useUpdateLead();
  const { modal } = App.useApp();

  const watchedName = Form.useWatch('customer_name', form) as string | undefined;
  const watchedEmail = Form.useWatch('email', form) as string | undefined;
  const watchedPhone = Form.useWatch('phone', form) as string | undefined;
  const watchedStatus = Form.useWatch('lead_status', form) as string | undefined;
  const dupQuery = useDebouncedValue(
    { customer_name: watchedName?.trim(), email: watchedEmail?.trim(), phone: watchedPhone?.trim() },
    500
  );
  const { data: duplicates } = useQuery({
    queryKey: ['lead-duplicates', dupQuery, entityId],
    queryFn: () => leadService.findDuplicates({ ...dupQuery, exclude_id: entityId }),
    enabled: isCreate && !!(dupQuery.customer_name || dupQuery.email || dupQuery.phone),
  });
  const duplicateCount = (duplicates?.leads.length ?? 0) + (duplicates?.customers.length ?? 0);

  const leadBreadcrumbSegments = useMemo(() => {
    if (isCreate || !entityId) return null;
    const label =
      (lead?.lead_number && String(lead.lead_number).trim()) ||
      entityId;
    return [
      { title: 'Leads', path: '/growth/leads' },
      { title: label, path: `/growth/leads/${entityId}/edit` },
    ];
  }, [isCreate, entityId, lead?.lead_number]);
  useDocumentBreadcrumb(leadBreadcrumbSegments);

  useEffect(() => {
    if (!entityId || !lead) return;
    form.setFieldsValue({
      customer_name: lead.customer_name,
      contact_person: lead.contact_person,
      email: lead.email,
      phone: lead.phone,
      source: lead.source,
      industry: lead.industry,
      lead_status: lead.lead_status,
      priority: lead.priority,
      assigned_to: lead.assigned_to || undefined,
      annual_revenue:
        lead.annual_revenue != null && Number.isFinite(Number(lead.annual_revenue))
          ? Number(lead.annual_revenue)
          : undefined,
      next_follow_up_date: lead.next_follow_up_date ? dayjs(lead.next_follow_up_date) : undefined,
      notes: lead.notes,
      business_challenge: (lead as { business_challenge?: string }).business_challenge,
      required_standards: (lead as { required_standards?: string }).required_standards,
      estimated_project_value: (lead as { estimated_project_value?: number }).estimated_project_value,
      expected_timeline_weeks: (lead as { expected_timeline_weeks?: number }).expected_timeline_weeks,
      qualification_level: (lead as { qualification_level?: number }).qualification_level ?? 1,
      strategic_importance: (lead as { strategic_importance?: string }).strategic_importance,
      revenue_service_type_id: lead.revenue_service_type_id || undefined,
      sales_team_id: lead.sales_team_id || undefined,
      customer_link: lead.customer_id ? { kind: 'customer', id: lead.customer_id } : null,
      status_reason: lead.status_reason || undefined,
      status_reason_notes: lead.status_reason_notes || undefined,
    });
  }, [entityId, lead?.id]);

  useEffect(() => {
    if (isCreate) {
      form.resetFields();
      form.setFieldsValue({
        lead_status: 'New',
        priority: 'Medium',
        assigned_to: currentUserId || undefined,
        sales_team_id: salesTeams.length === 1 ? salesTeams[0].id : undefined,
      });
    }
  }, [isCreate, form, salesTeams, currentUserId]);

  const buildPayload = (values: Record<string, unknown>): LeadInput => {
    const nextFollow = values.next_follow_up_date as Dayjs | undefined;
    return {
      customer_name: values.customer_name as string,
      contact_person: values.contact_person as string | undefined,
      email: values.email as string | undefined,
      phone: values.phone as string | undefined,
      source: values.source as string | undefined,
      industry: values.industry as string | undefined,
      lead_status: values.lead_status as string | undefined,
      priority: values.priority as string | undefined,
      assigned_to: (values.assigned_to as string | undefined) || null,
      annual_revenue:
        values.annual_revenue != null ? Number(values.annual_revenue) : undefined,
      next_follow_up_date: nextFollow ? nextFollow.format('YYYY-MM-DD') : null,
      notes: values.notes as string | undefined,
      business_challenge: values.business_challenge as string | undefined,
      required_standards: values.required_standards as string | undefined,
      estimated_project_value:
        values.estimated_project_value != null ? Number(values.estimated_project_value) : undefined,
      expected_timeline_weeks:
        values.expected_timeline_weeks != null ? Number(values.expected_timeline_weeks) : undefined,
      qualification_level:
        values.qualification_level != null ? Number(values.qualification_level) : undefined,
      strategic_importance: values.strategic_importance as string | undefined,
      revenue_service_type_id: (values.revenue_service_type_id as string | undefined) || null,
      sales_team_id: (values.sales_team_id as string | undefined) || null,
      customer_id: (values.customer_link as CrmRecordLink | null | undefined)?.id ?? null,
      ...(LEAD_CLOSED_STATUSES.includes(String(values.lead_status))
        ? {
            status_reason: (values.status_reason as string | undefined) ?? null,
            status_reason_notes: (values.status_reason_notes as string | undefined)?.trim() || null,
          }
        : {}),
    };
  };

  const save = async (payload: LeadInput) => {
    if (isCreate) {
      const created = await createLead.mutateAsync(payload);
      navigate(`/growth/leads/${created.id}/edit`, { replace: true });
    } else if (entityId) {
      await updateLead.mutateAsync({ id: entityId, data: payload });
    }
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    const payload = buildPayload(values);
    if (isCreate) {
      const found = await leadService.findDuplicates({
        customer_name: payload.customer_name,
        email: payload.email,
        phone: payload.phone,
      });
      if (found.leads.length || found.customers.length) {
        modal.confirm({
          title: 'Possible duplicate',
          width: 560,
          content: <DuplicateList matches={found} />,
          okText: 'Create anyway',
          cancelText: 'Go back',
          onOk: () => save(payload),
        });
        return;
      }
    }
    await save(payload);
  };

  if (!isCreate && entityId && loadingLead) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/growth/leads')}>
          Back to leads
        </Button>
      </Space>
      <Title level={3}>{isCreate ? 'New lead' : 'Edit lead'}</Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
        {isCreate ? 'Create a lead; you can attach documents after saving.' : `Lead ${lead?.lead_number ?? ''}`}
      </Text>

      <Card style={{ marginBottom: 24 }}>
        {isCreate && duplicateCount > 0 && duplicates && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={`${duplicateCount} existing record(s) look like this lead`}
            description={<DuplicateList matches={duplicates} />}
          />
        )}
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="customer_name"
                label="Customer / company"
                rules={[{ required: true, message: 'Required' }]}
              >
                <Input placeholder="Company name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="contact_person" label="Contact person">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="email" label="Email" rules={[{ type: 'email', message: 'Invalid email' }]}>
                <Input prefix={<MailOutlined />} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="phone" label="Phone">
                <Input prefix={<PhoneOutlined />} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="business_challenge"
                label="Business challenge"
                tooltip="What problem is the customer trying to solve?"
              >
                <TextArea rows={3} placeholder="Describe the customer's operational or business challenge" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="required_standards" label="Required standards">
                <Input placeholder="e.g. API 6D, ASME" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="estimated_project_value" label="Estimated project value">
                <Input type="number" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="expected_timeline_weeks" label="Expected timeline (weeks)">
                <Input type="number" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="qualification_level" label="Qualification level">
                <Select
                  options={[
                    { value: 1, label: '1 — Basic' },
                    { value: 2, label: '2 — Business' },
                    { value: 3, label: '3 — Technical' },
                    { value: 4, label: '4 — Strategic' },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="strategic_importance" label="Strategic importance">
                <Select
                  allowClear
                  options={['Low', 'Medium', 'High', 'Critical'].map((x) => ({ label: x, value: x }))}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="source" label="Source">
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  options={['Website', 'Referral', 'Cold Call', 'Exhibition', 'LinkedIn', 'Other'].map((x) => ({
                    label: x,
                    value: x,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="industry" label="Industry">
                <Input allowClear />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="annual_revenue" label="Annual revenue">
                <Input type="number" placeholder="Optional" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="priority" label="Priority" rules={[{ required: true }]}>
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={PRIORITIES.map((p) => ({ label: p, value: p }))}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="lead_status" label="Status">
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={LEAD_STATUSES.map((s) => ({ label: s, value: s }))}
                />
              </Form.Item>
            </Col>
            {watchedStatus && LEAD_CLOSED_STATUSES.includes(watchedStatus) && (
              <>
                <Col span={8}>
                  <Form.Item
                    name="status_reason"
                    label={`${watchedStatus} reason`}
                    rules={[{ required: true, message: 'Choose a reason' }]}
                  >
                    <Select options={LEAD_STATUS_REASONS.map((r) => ({ value: r, label: r }))} />
                  </Form.Item>
                </Col>
                <Col span={16}>
                  <Form.Item
                    noStyle
                    shouldUpdate={(a, b) => a.status_reason !== b.status_reason}
                  >
                    {({ getFieldValue }) => (
                      <Form.Item
                        name="status_reason_notes"
                        label="Reason details"
                        rules={[{ required: getFieldValue('status_reason') === 'Other', message: 'Describe the reason' }]}
                      >
                        <Input placeholder="Optional unless Other" />
                      </Form.Item>
                    )}
                  </Form.Item>
                </Col>
              </>
            )}
            <Col span={8}>
              <Form.Item name="sales_team_id" label="Sales team" rules={[{ required: true, message: 'Required' }]}>
                <Select showSearch optionFilterProp="label" options={teamOptions} placeholder="Select team" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="assigned_to" label="Assigned to">
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder="Sales executive, manager, director, or GM sales"
                  notFoundContent="No users with a sales owner role"
                  options={users.map((u) => ({
                    label: `${u.first_name} ${u.last_name || ''}`.trim(),
                    value: u.id,
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="revenue_service_type_id"
                label="Revenue service type"
                rules={[{ required: true, message: 'Required — flows to opportunity, quotation, customer order, and invoice' }]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder="Select revenue / service type for this lead"
                  options={rstOptions}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="next_follow_up_date" label="Next follow-up">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="customer_link"
                label="Existing customer"
                tooltip="Link the lead to a customer master record so it shows in Customer 360. Set automatically on conversion."
              >
                <CrmRecordPicker kinds={['customer']} valueLabel={lead?.customer_name} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="notes" label="Notes">
            <TextArea rows={3} />
          </Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={createLead.isPending || updateLead.isPending}>
              Save
            </Button>
            <Button onClick={() => navigate('/growth/leads')}>Cancel</Button>
          </Space>
        </Form>
      </Card>

      {entityId ? (
        <Card title="Attachments">
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <DocumentUpload entityType="Lead" entityId={entityId} />
            <DocumentList entityType="Lead" entityId={entityId} />
          </Space>
        </Card>
      ) : null}
    </div>
  );
};

export default LeadFormPage;
